import { useEffect, useState } from "react";
import { Check, CheckCheck } from "lucide-react";
import { FormWrapper } from "@/components/molecules/FormWrapper/FormWrapper";
import { FormSelect } from "@/components/molecules/FormSelect/FormSelect";
import { VariableOrdersFormProps } from "./VariableOrdersForm.types";
import { mapToFormValues } from "../infrastructure/VariableOrderMapper";
import { InstrumentDetail } from "./InstrumentDetail";
import { FormInput } from "@/components/molecules/FormInput/FormInput";
import { FormAutocomplete } from "@/components/molecules/FormAutocomplete/FormAutocomplete";
import { Badge } from "@/components/atoms/Badge";
import { OrderStatusEnum, OrderTypeEnum, Portfolio, LimitTypeEnum } from "../domain/entities/OrdersEntity";
import { Button } from "@/components/atoms/Button";
import { useConfirmDialogs } from "@/core/context/DialogContext";
import { FormSwitch } from "@/components/molecules/FormSwitch";
import { PortfolioDetail } from "./PortfolioDetail";
import { schema, VariableOrderFormData, variantMap } from "../infrastructure/VariableOrdersConstants";

export const VariableOrdersForm = ({
    onSubmit,
    onDelete,
    onContinue,
    orderSelected,
    portfolioOptions,
    instrumentOptions,
    instrumentSelected,
    portfolios,
    setInstrumentSelected,
    formStatus
}: VariableOrdersFormProps) => {
    const [quantity, setQuantity] = useState(0);
    const [limitPrice, setLimitPrice] = useState(0);
    const [isOn, setIsOn] = useState(orderSelected ? orderSelected.orderType === OrderTypeEnum.COMPRA : true);
    const [limitType, setLimitType] = useState(orderSelected ? orderSelected.limitType === LimitTypeEnum.MERCADO : true);
    const [portfolioSelected, setPortfolioSelected] = useState<Portfolio | undefined>(orderSelected?.portfolio);
    const variant = variantMap[orderSelected?.status as unknown as OrderStatusEnum] ?? "info";
    const showDialog = useConfirmDialogs();

    const confirmSubmit = async (formData: VariableOrderFormData) => {
        onSubmit(formData)
    }

    const confirmDelete = async (id: string) => {
        const confirm = await showDialog({
            title: "¿Estás seguro?",
            description: "Esta acción eliminará el registro. ¿Deseas continuar?",
            variant: "delete",
        });

        if (confirm) {
            onDelete(id);
        }
    }

    return (
        <FormWrapper<VariableOrderFormData>
            defaultValues={mapToFormValues(orderSelected)}
            onSubmit={confirmSubmit}
            schema={schema}
            formStatus={formStatus}
            onDelete={confirmDelete}
            canSave={orderSelected?.status !== OrderStatusEnum.FIN}
        >
            {({ setValue }) => {
                const handleOnLimitTypeChange = (limitType: boolean) => {
                    setLimitType(limitType);
                    if (limitType) {
                        setValue("limitPrice", 0);
                        setValue("value", 0);
                    }
                }

                useEffect(() => {
                    setValue("value", (!!quantity ? quantity : 0) * (!!limitPrice ? limitPrice : 0));
                }, [quantity, limitPrice])


                return (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <FormSwitch
                                label={isOn ? OrderTypeEnum.COMPRA : OrderTypeEnum.VENTA}
                                variant="darkInfo"
                                inactiveVariant="darkError"
                                onCheckedChange={setIsOn}
                                name="orderType" />

                            {orderSelected && (
                                <div className="flex flex-col-reverse md:flex-row md:flex-wrap justify-end gap-2 items-center">
                                    {orderSelected?.confirmNumber && (
                                        <Badge className="w-full md:w-auto gap-2 px-4 justify-between" variant="secondary">
                                            {`N° Operación: ${orderSelected?.confirmNumber}`}
                                        </Badge>
                                    )}

                                    <div className="flex gap-2 w-full md:w-auto">
                                        <Badge className="w-full" variant={variant}>
                                            {orderSelected?.status}
                                        </Badge>

                                        {orderSelected?.status !== OrderStatusEnum.FIN && (
                                            <Button
                                                size="xs"
                                                onClick={() =>
                                                    onContinue(
                                                        mapToFormValues(orderSelected) as VariableOrderFormData,
                                                        orderSelected?.status === OrderStatusEnum.REGISTRO
                                                            ? OrderStatusEnum.COMPLEMENTACION
                                                            : OrderStatusEnum.FIN
                                                    )
                                                }
                                            >
                                                {orderSelected?.status === OrderStatusEnum.REGISTRO ? (
                                                    <Check className="h-6 w-6" aria-hidden="true" />
                                                ) : (
                                                    <CheckCheck className="h-6 w-6" aria-hidden="true" />
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div >

                        <div className="grid grid-cols-1 gap-2">
                            <FormSelect
                                onChange={(value: string) => {
                                    const portfolio = portfolios.find(pf => pf.id === value);
                                    if (portfolio) {
                                        setPortfolioSelected(portfolio);
                                    }
                                }}
                                name="portfolio"
                                label="Portafolio"
                                options={portfolioOptions}
                            />
                            {!!portfolioSelected && (
                                <PortfolioDetail portfolio={portfolioSelected} />
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-1 gap-2">
                            <FormAutocomplete
                                name="instrument"
                                label="Instrumento"
                                options={instrumentOptions}
                                onChange={setInstrumentSelected}
                                renderOption={(option) => (
                                    <span className="flex flex-col text-left h-full">
                                        <small className="font-bold">{option.label}</small>
                                        <small>{option.detail}</small>
                                    </span>
                                )}
                            />
                            {instrumentSelected && (
                                <InstrumentDetail instrument={instrumentSelected} />
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <FormSwitch
                                label={limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE}
                                inactiveVariant="warning"
                                onCheckedChange={handleOnLimitTypeChange}
                                name="limitType" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <FormInput
                                name="quantity"
                                label="Cantidad"
                                type="number"
                                onChange={({ target }) => setQuantity(parseInt(target.value))}
                            />
                            {!limitType && (
                                <>
                                    <FormInput
                                        name="limitPrice"
                                        label="Precio límite"
                                        type="number"
                                        prefix="$"
                                        onChange={({ target }) => setLimitPrice(parseInt(target.value))}
                                    />
                                    <FormInput name="value" label="Valor giro" type="number" prefix="$" disabled />
                                </>
                            )}
                        </div>
                    </>
                );
            }}
        </FormWrapper >
    );
};